"use client";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { PlannerPreferences } from "@/lib/planner/preferences";
import type { AreaSport } from "@/lib/planner/graph-types";
import { useFeedbackAccount } from "@/app/_components/companion/FeedbackAccount";

/** Reviewed account suggestions stay in this visit, outside browser-wide defaults. */
export function useOutingPreferences(
  slug: string,
  sport: AreaSport,
  base: PlannerPreferences,
  setBase: Dispatch<SetStateAction<PlannerPreferences>>,
) {
  const { context } = useFeedbackAccount();
  const key =
    context && context.owner !== "device"
      ? `${context.owner}:${context.generation}:${slug}`
      : null;
  const [overlay, setOverlay] = useState<{
    key: string;
    prefs: PlannerPreferences;
  } | null>(null);
  const active = !!key && overlay?.key === key && overlay.prefs.sport === sport;
  const prefs = active ? overlay.prefs : base;
  const latest = useRef({ key, prefs });
  useLayoutEffect(() => {
    latest.current = { key, prefs };
  }, [key, prefs]);
  const setPrefs = useCallback<Dispatch<SetStateAction<PlannerPreferences>>>(
    (action) => {
      if (active)
        setOverlay((previous) =>
          previous && previous.key === key
            ? {
                ...previous,
                prefs:
                  typeof action === "function"
                    ? action(previous.prefs)
                    : action,
              }
            : previous,
        );
      else setBase(action);
    },
    [active, key, setBase],
  );
  const apply = useCallback(
    (next: PlannerPreferences, expected: string, expectedKey: string) => {
      const current = latest.current;
      if (
        !current.key ||
        current.key !== expectedKey ||
        JSON.stringify(current.prefs) !== expected ||
        next.sport !== current.prefs.sport
      )
        throw new Error(
          "Your account or planner choices changed. Review the suggestions again.",
        );
      setOverlay({ key: current.key, prefs: next });
    },
    [],
  );
  return {
    context,
    ownerKey: key,
    prefs,
    setPrefs,
    apply,
    temporary: active,
    clear: () => setOverlay(null),
  };
}
